<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="logs">
					<app-heading :title="`${integration.name} - Logs`" />
					<app-filter :value="hasFilters" @click="applyFilters()">
						<app-row no-padding>
							<app-cell cols="6" sm="4">
								<input-select
									v-model="filters.integration_connection"
									label="Connections"
									:items="connections"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="4">
								<input-select
									v-model="filters.status"
									label="Status"
									:items="statuses"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="4">
								<input-text
									v-model="filters.unit_code"
									label="Unit Code"
									clearable
									dense
								></input-text>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="logs"
								:total="count"
								:headers="headers"
								:sort-by="'ts'"
								:sort-desc="true"
								@update="fetchLogs"
							>
								<template v-slot:status="{ item }">
									<span
										v-if="item.status === 'warning'"
										class="warning--text"
									>Warning</span>
									<span
										v-if="item.status === 'success'"
										class="success--text"
									>Success</span>
									<span
										v-if="item.status === 'error'"
										class="error--text"
									>Error</span>
								</template>
								<template v-slot:integration_connection="{ item }">
									<div>{{ getConnectionName(item.integration_connection) }}</div>
								</template>
								<template v-slot:unit_code="{ item }">
									<span v-if="item.unit_code">{{ item.unit_code }}</span>
									<i v-else>None</i>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										table
										@click="onLogDetails(item._id)"
									>
										Details
									</app-button>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>
			</template>
		</app-tabs>

		<app-dialog
			v-model="log.show"
			max-width="800"
			height="600"
			max-height="600"
			title="Log details"
		>
			<app-dialog-body>
				<app-row>
					<app-cell xs6>
						<div><b>Request</b></div>
						<app-json :data="log.request" open />
					</app-cell>
					<app-cell xs1></app-cell>
					<app-cell xs5>
						<div><b>Details</b></div>
						<app-json :data="log.details" open />
					</app-cell>
				</app-row>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat="flat"
					@click="log.show = false"
				>Close</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import filters from '@/mixins/filters'
import { mapSelectOptions, dynamicNewMapEntries } from '@/utils/maps'
import { integrations as integrationTabs } from '@/datasources/tabs'

export default {
	mixins: [filters],
	async created() {
		try {
			this.$store.dispatch('app/startLoading')
			this.integration = await this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.integrationId)
				.fields(['name'])
				.get()

			this.$store.dispatch('app/stopLoading')
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}

		this.tabs = integrationTabs(this.integrationId)
		this.connections = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.to(this.integrationId)
			.to('connections')
			.field(['name'])
			.limit(100)
			.get())
		/*
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get()) */
	},
	data() {
		return {
			tabs: [],
			logs: [],
			log: {
				show: false,
				request: {},
				details: [],
			},
			connectionsMap: {},
			connections: [],
			integration: {
				name: '',
			},
			count: -1,
			statuses: [{
				text: 'Success',
				value: 'success',
			}, {
				text: 'Warning',
				value: 'warning',
			}, {
				text: 'Error',
				value: 'error',
			}],
			headers: [{
				text: 'Timestamp',
				value: 'ts',
				sortable: true,
				format: 'datetime',
			}, {
				text: 'status',
				value: 'status',
				sortable: true,
			}, {
				text: 'Connection',
				value: 'integration_connection',
				sortable: true,
			}, {
				text: 'Unit',
				value: 'unit_code',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			filters: {
				integration_connection: '',
				unit_code: '',
				status: '',
			},
		}
	},
	computed: {
		integrationId() {
			return this.$route.params.integration
		},
	},
	methods: {
		getConnectionName(id) {
			return this.connectionsMap[id] && this.connectionsMap[id].name
				? this.connectionsMap[id].name
				: 'UNDEFINED'
		},

		async updateConnectionsMap() {
			const res = await dynamicNewMapEntries(
				this.logs,
				'integration_connection',
				this.connectionsMap,
				this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.integrationId)
					.to('connections')
					.field('name'),
			)

			if (res.err) {
				this.$notifications.pushAlert('error', res.err.data ? res.err.data.message : res.err.message)
			}
			this.connectionsMap = {
				...this.connectionsMap,
				...res.entries,
			}
		},

		async fetchLogs(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.integrationId)
				.to('logs')
				.fromPagination(pagination)
				.fields([
					'ts',
					'status',
					'integration_connection',
					'unit_code',
				])

			if (this.appliedFilters.integration_connection) {
				request.param('integration_connection', this.appliedFilters.integration_connection)
			}
			if (this.appliedFilters.unit_code) {
				request.param('unit_code', this.appliedFilters.unit_code)
			}
			if (this.appliedFilters.status) {
				request.param('status', this.appliedFilters.status)
			}
			try {
				this.logs = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}

				await this.updateConnectionsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async onLogDetails(id) {
			this.$store.dispatch('app/startLoading')
			const request = this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.integrationId)
				.to('logs')
				.to(id)

			try {
				const log = await request.get()
				this.log.request = log.request
				this.log.details = log.details
				this.log.show = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
